import { useDictation } from "@/hooks/useDictation";
import * as React from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import CompassLogo from "./CompassLogo";
// Textarea will be lazy loaded below
import ReactMarkdown from "react-markdown";
import { Button } from "./ui/button";
import { Send, Mic, Loader, StopCircle } from "lucide-react";
import { useDocument } from "../../hooks/useDocument";
import { ICompasAIChatAction, ICompassMessage } from "../../types/compass.types";
import { compasAIQueryStream } from "../../services/compass.service";
import {
  useCompassChatHistory,
  useInvalidateCompassChat,
  useTransformCompassHistory,
} from "../../hooks/useCompassChat";
const CompassMessage = React.lazy(() => import("./CompassMessage"));
const MessageLoading = React.lazy(() => import("./MessageLoading"));
const ActionItems = React.lazy(() => import("./ActionItems"));
const SoundWave = React.lazy(() => import("./SoundWave"));
const Textarea = React.lazy(() => import("./ui/textarea").then((module) => ({ default: module.Textarea })));
import ScrollToBottomButton from "./ScrollToBottomButton";

interface CompassDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const CompassDialog: React.FC<CompassDialogProps> = ({ isOpen, onClose }) => {
  const [chatMessage, setChatMessage] = React.useState("");
  const [isAssistantTyping, setIsAssistantTyping] = React.useState(false);
  const [optimisticMessages, setOptimisticMessages] = React.useState<ICompassMessage[]>([]);

  const { data: doc } = useDocument();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const { data: chatHistory, isLoading } = useCompassChatHistory(doc?._id || "");
  const historyMessages = useTransformCompassHistory(chatHistory);
  const invalidateCompassChat = useInvalidateCompassChat();

  const { isRecording, isMicroLoading, isConverting, startDictation, isSpeaking } = useDictation({
    onTranscript: setChatMessage,
  });

  React.useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const scrollContainer = scrollContainerRef.current;

      // Check if we are near the bottom before resizing
      const isNearBottom = scrollContainer
        ? scrollContainer.scrollHeight - scrollContainer.scrollTop <= scrollContainer.clientHeight + 50
        : false;

      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;

      // If we were near the bottom, scroll to the bottom after resize to prevent "crawling"
      if (isNearBottom && scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [chatMessage, isSpeaking]);

  const handleMicClick = () => {
    startDictation();
  };

  const messages = React.useMemo(() => {
    return [...historyMessages, ...optimisticMessages];
  }, [historyMessages, optimisticMessages]);

  const prevMessagesLengthRef = React.useRef(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current || isAssistantTyping) {
      scrollToBottom();
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length, isAssistantTyping]);

  const handleSendMessage = async () => {
    if (!chatMessage.trim() || !doc?._id || !doc?.title) return;

    const userMessage = chatMessage.trim();
    setChatMessage("");

    if (textareaRef.current) {
      textareaRef.current.style.height = "20px";
    }

    const optimisticUserMessage: ICompassMessage = {
      isSender: true,
      canCopy: false,
      text: userMessage,
      creation_date: new Date().toISOString(),
    };

    setOptimisticMessages((prev) => [...prev, optimisticUserMessage]);

    setIsAssistantTyping(true);

    try {
      const payload = {
        text: userMessage,
        document_ids: [doc._id],
        current_document_id: doc._id,
        document_title: [doc.title],
      };

      const stream = await compasAIQueryStream(payload);
      const reader = stream.getReader();
      const decoder = new TextDecoder();

      let assistantResponse = "";
      let actions: ICompasAIChatAction[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter((line) => line.trim());

        for (const line of lines) {
          try {
            const data = JSON.parse(line);

            if (
              data.type === "message" ||
              data.type === "question"
            ) {
              assistantResponse += data.response || "";
            } else if (data.type === "action") {
              actions.push(data as ICompasAIChatAction);
            }
          } catch (e) {
            assistantResponse += line;
          }
        }
      }

      setIsAssistantTyping(false);
      setOptimisticMessages([]);

      if (doc?._id) {
        invalidateCompassChat(doc._id);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      setIsAssistantTyping(false);
      setOptimisticMessages([]);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setChatMessage(event.target.value);
    const textarea = event.target;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-11/12 max-h-[90vh] h-full flex flex-col p-0">
        <DialogHeader className="flex-row items-center justify-between px-4 py-2 border-b">
          <div className="flex items-center gap-3">
            <CompassLogo containerClassName="size-10" />
            <DialogTitle className="text-base font-medium">AI Compass</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 relative min-h-0 flex flex-col">
            <div className="flex-1 overflow-y-auto px-2 space-y-4" ref={scrollContainerRef}>
              {messages.length === 0 && !isLoading && !isAssistantTyping && (
                <div className="bg-neutral-700 text-neutral-200 px-4 py-3 rounded-lg text-sm flex flex-col gap-2 w-full">
                  <div className="flex flex-col items-center gap-4">
                    <CompassLogo containerClassName="size-24" size="lg" />
                    <div className="flex-1 prose prose-sm prose-li:marker:text-neutral-200 max-w-none">
                      <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" />
                          ),
                        }}
                      >
                        {`**Hi! I'm Compass,**

I am your legal project manager.

1. Please tell me the context of the situation.
2. And the tasks you want done or outcome(s) you are looking to achieve.

I'll create a plan and instruct the other AI's to do the work for you.

Or, if you are not sure where to start, just let me know.`}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}

              {isLoading && messages.length === 0 && (
                <div className="flex justify-center p-6">
                  <Loader className="animate-spin" />
                  <span>Loading chat history...</span>
                </div>
              )}


              {messages.map((message, idx) => (
                <React.Fragment key={idx}>
                  {!message.isAction ? (
                    <motion.div
                      initial={{ opacity: 0, x: message.isSender ? 20 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="w-full"
                    >
                      <React.Suspense fallback={<div className="h-10" />}>
                        <CompassMessage
                          message={message}
                          type={message.isSender ? "user" : "assistant"}
                        />
                      </React.Suspense>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="w-full"
                    >
                      <React.Suspense fallback={<div className="h-20" />}>
                        <ActionItems message={message} messages={messages} />
                      </React.Suspense>
                    </motion.div>
                  )}
                </React.Fragment>
              ))}
              {isAssistantTyping && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="w-full"
                >
                  <React.Suspense fallback={<div className="h-8" />}>
                    <MessageLoading text="AI is thinking" />
                  </React.Suspense>
                </motion.div>
              )}


              <div ref={messagesEndRef} />
            </div>
            <ScrollToBottomButton
              targetRef={messagesEndRef}
              containerRef={scrollContainerRef}
              dependency={messages}
            />
          </div>

          <DialogFooter className="flex flex-row items-end gap-2 p-4 border-t">
            <div className="relative flex-1">
              <div
                className="relative items-center pr-1.5 min-h-10 flex border rounded-md overflow-hidden animate-border-pulse focus-within:animate-none transition-shadow duration-300"
              >
                {(isRecording || isMicroLoading) && isSpeaking ? (
                  <div className="flex-1 flex items-center justify-center h-10">
                    <React.Suspense fallback={<div className="h-10 w-full animate-pulse bg-neutral-100 rounded" />}>
                      <SoundWave />
                    </React.Suspense>
                  </div>
                ) : (
                  <React.Suspense fallback={<div className="h-10 w-full animate-pulse bg-neutral-100 rounded" />}>
                    <Textarea
                      ref={textareaRef}
                      className="flex-1 border-none focus:ring-0 focus-visible:ring-0 p-0 outline-none resize-none pl-2 bg-transparent text-sm min-h-5 shadow-none py-1.5 placeholder:text-neutral-600"
                      placeholder="Type or Dictate your outcome."
                      value={chatMessage}
                      onChange={handleTextareaChange}
                      onKeyDown={handleKeyPress}
                      rows={1}
                    />
                  </React.Suspense>
                )}
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {isRecording || isMicroLoading ? (
                <Button
                  size="icon"
                  variant="destructive"
                  className="size-10 p-0"
                  onClick={handleMicClick}
                  title="Stop recording"
                >
                  <StopCircle />
                </Button>
              ) : chatMessage.trim() === "" ? (
                <Button
                  size="icon"
                  className="size-10 p-0 bg-compass-action hover:bg-compass-action/90"
                  onClick={handleMicClick}
                  disabled={isMicroLoading || isConverting}
                  title="Voice message"
                >
                  {isMicroLoading ? (
                    <Loader className="animate-spin" />
                  ) : (
                    <Mic />
                  )}
                </Button>
              ) : (
                <Button
                  size="icon"
                  className="size-10 p-0 bg-compass-action hover:bg-compass-action/90"
                  onClick={handleSendMessage}
                  disabled={!chatMessage.trim() || isAssistantTyping}
                  title="Send message"
                >
                  <Send />
                </Button>
              )}
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompassDialog;
